import { isEmptyStep, mergeSteps, type HistoryStep } from '@dispositio/shared';
import { create } from 'zustand';

/**
 * Verlauf einer Geschichte (Caretaker des Memento-Musters): Schritte zum Rückgängigmachen und Wiederholen. Jeder Schritt hält den
 * Stand der berührten Datensätze vorher und nachher (siehe `stepOf`); zurückgeschrieben wird er über den Server (`/history`).
 */

/** Höchstzahl der Schritte, die man zurückgehen kann */
export const HISTORY_LIMIT = 100;
/** Änderungen am selben Feld (Tippen mit Autosave), die so kurz aufeinander folgen, zählen als ein Schritt */
export const COALESCE_MS = 4000;

export interface Recorded {
  /** Was der Schritt getan hat („Verschieben: „Anna““) */
  label: string;
  /** Gleicher Schlüssel kurz nacheinander: ein Schritt (z. B. `doc:<id>` beim Schreiben) */
  key: string | null;
  at: number;
  step: HistoryStep;
}

interface HistoryState {
  projectId: string | null;
  undo: Recorded[];
  redo: Recorded[];
  /** Rückgängig bzw. Wiederholen läuft gerade */
  busy: boolean;
  /** Offene Gruppe (mehrere Aktionen einer Geste werden ein Schritt) */
  group: { depth: number; label: string; open: boolean };
  /** Auf eine Geschichte einstellen: wechselt sie, beginnt der Verlauf leer */
  use: (projectId: string) => void;
  /** Neuen Schritt festhalten (eine neue Änderung macht das Wiederholen hinfällig) */
  record: (projectId: string, label: string, key: string | null, step: HistoryStep) => void;
  beginGroup: (label: string) => void;
  endGroup: () => void;
  clear: () => void;
  setBusy: (busy: boolean) => void;
  /** Nach Rückgängig: der Schritt kommt auf den Stapel zum Wiederholen (und umgekehrt) */
  moved: (from: 'undo' | 'redo') => Recorded | null;
}

export const useHistory = create<HistoryState>((set, get) => ({
  projectId: null,
  undo: [],
  redo: [],
  busy: false,
  group: { depth: 0, label: '', open: false },
  use: (projectId) => {
    if (get().projectId !== projectId) set({ projectId, undo: [], redo: [], busy: false, group: { depth: 0, label: '', open: false } });
  },
  record: (projectId, label, key, step) => {
    const s = get();
    if (s.projectId !== projectId || isEmptyStep(step)) return;
    const now = Date.now();
    const last = s.undo[s.undo.length - 1];
    // in einer offenen Gruppe: alles in ihren Schritt
    if (s.group.depth > 0) {
      if (s.group.open && last) {
        set({ undo: [...s.undo.slice(0, -1), { ...last, step: mergeSteps(last.step, step), at: now }], redo: [] });
      } else {
        set({ undo: [...s.undo, { label: s.group.label || label, key: null, at: now, step }].slice(-HISTORY_LIMIT), redo: [], group: { ...s.group, open: true } });
      }
      return;
    }
    // Tippen: dieselbe Stelle kurz nacheinander ergänzt den letzten Schritt
    if (key && last && last.key === key && now - last.at < COALESCE_MS) {
      const merged = mergeSteps(last.step, step);
      set({ undo: isEmptyStep(merged) ? s.undo.slice(0, -1) : [...s.undo.slice(0, -1), { ...last, step: merged, at: now }], redo: [] });
      return;
    }
    set({ undo: [...s.undo, { label, key, at: now, step }].slice(-HISTORY_LIMIT), redo: [] });
  },
  beginGroup: (label) => {
    const g = get().group;
    set({ group: g.depth > 0 ? { ...g, depth: g.depth + 1 } : { depth: 1, label, open: false } });
  },
  endGroup: () => {
    const g = get().group;
    set({ group: g.depth > 1 ? { ...g, depth: g.depth - 1 } : { depth: 0, label: '', open: false } });
  },
  clear: () => set({ undo: [], redo: [] }),
  setBusy: (busy) => set({ busy }),
  moved: (from) => {
    const s = get();
    const list = from === 'undo' ? s.undo : s.redo;
    const top = list[list.length - 1];
    if (!top) return null;
    const rest = list.slice(0, -1);
    // ein zurückgenommener Schritt lässt sich nicht mehr mit späterem Tippen zusammenfassen
    const moved = { ...top, key: null };
    set(from === 'undo' ? { undo: rest, redo: [...s.redo, moved] } : { redo: rest, undo: [...s.undo, moved] });
    return top;
  },
}));
